/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 *
 */
import styled, { css, keyframes } from 'styled-components';
import StyledProps from '../../common/theme/AppThemeInterface';
import {
  TERMINAL_ICON_CLASSNAME,
  TERMINAL_ICON_SECOND_RING_CLASSNAME,
  TERMINAL_ICON_THIRD_RING_CLASSNAME,
} from '../../fleet/TerminalIconStaticMarker';
import { LEG_WRAPPER_Z_INDEX } from './RulerPath';

export const CIRCLE_SIZE = 28;
export const TAIL_TAG_CIRCLE_OVERLAP_PX = 8;
export const TAIL_TAG_HEIGHT = 76;
const TAIL_TAG_HEADER_HEIGHT = 36;
// Large number to be max z index for selected tail tag
// Updated from 90000 to 16000000 because the hubs marker clusterer sets a z-index of 1000001 to its cluster markers
export const Z_INDEX_MAX = 160000000; // large number to be max z index for selected tail tag
export const TAIL_TAG_OFFLINE_CLASS = 'map__tailtag--offline';
export const TAIL_TAG_ONLINE_CLASS = 'map__tailtag--online';
export const DISPLAY_NONE_CLASS = 'map__display--none';
export const DISPLAY_BLOCK_CLASS = 'map__display--block';

const keyFrameAircraftPulse = keyframes`
  0% {
    transform: scale(0.2);
    opacity: 0;
  }
  33% {
    transform: scale(0.6);
    opacity: 0.3;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
`;

export interface BaseMapWrapperProps {
  width?: string;
  height?: string;
  left?: string;
  top?: string;
  isHidden?: boolean;
}

const BaseMapWrapper = styled.div`
  ${({ width, left, height, top, isHidden }: BaseMapWrapperProps) => css`
    height: ${height ? height : '100%'};
    left: ${left ? left : '0px'};
    width: ${width ? width : '100%'};
    top: ${top ? top : '0px'};
    display: ${isHidden ? 'none' : 'block'};
  `};
  overflow: unset !important;
  position: absolute;
  min-width: 332px;
  z-index: 0;

  > div:first-child {
    overflow: hidden;
  }

  @media print {
    width: 100%;
    left: 0;
  }

  .googleMap {
    width: 100%;
    height: 100%;
  }
  .terminals_in_your_beam_button_panel_btn_count_badge {
    background-color: ${({ theme }: StyledProps) => theme.buttonSelectedColor};
    border: 1px solid ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
    color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
    position: absolute;
    height: 22px;
    width: 22px;
    top: -8px;
    left: -8px;
    border-radius: 15px;
    line-height: 24px;
    letter-spacing: -0.08px;
  }
  .terminals_in_your_beam_button_panel_btn_count_badge.hidden {
    visibility: hidden;
  }
  .terminals_in_your_beam_button_panel_btn_count_badge.visible {
    visibility: visible;
  }
  .terminals_in_your_beam_button_panel_btn_error_badge.hidden {
    visibility: hidden;
  }
  .terminals_in_your_beam_button_panel_btn_error_badge.visible {
    visibility: visible;
  }
  .terminals_in_your_beam_button_panel_btn--active, #mapFullScreenBtn.alternateIconShown--active,
  .performance_outlook_legend_panel_icon--active, .tail_tag_overlays_panel_icon--active, .overlays_panel_icon--active, .map_settings_icon--active, .map_ruler_tool_icon--active {
    color: ${({ theme }: StyledProps) => theme.buttonSelectedColor}
  }

  .map__aircraftcontainer {
    cursor: pointer;
    margin-top: -14px;
    margin-left: -14px;
    transform: translateZ(0px);
    display: inline-block;
  }

  .map__aircraftcontainer:hover {
    z-index: ${Z_INDEX_MAX} !important;

    .map__tailtag {
      flex-direction: row;
      opacity: 1 !important;
    }
  }

  .${TAIL_TAG_ONLINE_CLASS}:not(.map__tailtag--selected), .${TAIL_TAG_OFFLINE_CLASS}:not(.map__tailtag--selected)  {
    background-color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};
  }

  .map__tailtag--selected.${TAIL_TAG_ONLINE_CLASS}, .map__tailtag--selected.${TAIL_TAG_OFFLINE_CLASS} {
    background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor};
  }

  .${TAIL_TAG_ONLINE_CLASS}:hover, .${TAIL_TAG_OFFLINE_CLASS}:hover  {
    background-color: ${({ theme }: StyledProps) => theme.secondaryBackgroundColor} !important;
  }

  .${TERMINAL_ICON_SECOND_RING_CLASSNAME} {
    display: none;
  }

  .${TERMINAL_ICON_THIRD_RING_CLASSNAME} {
    display: none;
  }

  .map__aircraftcontainer--selected {
    z-index: ${Z_INDEX_MAX - 1} !important;

    .${TERMINAL_ICON_CLASSNAME} {
      box-shadow: 0 0 0 0;
    }

    .pulse {
      border-radius: 50px;
      height: 60px;
      left: 14px;
      letter-spacing: 0.05em;
      line-height: 50px;
      position: absolute;
      text-align: center;
      text-transform: uppercase;
      top: 14px;
      transform: translate(-50%, -50%);
      width: 60px;
    }

    .pulse:before {
      -webkit-animation: ${keyFrameAircraftPulse} 1.875s infinite linear;
      animation: ${keyFrameAircraftPulse} 1.875s infinite linear;
      background: ${({ theme }: StyledProps) => theme.primaryTextColor};
      border-radius: 50px;
      content: '';
      height: 100%;
      left: 0;
      opacity: 0;
      position: absolute;
      top: 0;
      transform: scale(1);
      width: 100%;
    }

    .pulse:after {
      -webkit-animation: ${keyFrameAircraftPulse} 1.875s 0.975s infinite linear;
      animation: ${keyFrameAircraftPulse} 1.875s 0.975s infinite linear;
      background: ${({ theme }: StyledProps) => theme.primaryTextColor};
      border-radius: 50px;
      content: '';
      height: 100%;
      left: 0;
      opacity: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
  }

  .map__aircraftcontainer--below_ruler_components {
    z-index: ${LEG_WRAPPER_Z_INDEX - 1} !important;
  }
  .map__aircraftcontainer--transparent .map__tailtag--container {
    opacity: 0.8;
  }
  .map__aircraftcontainer--transparent:not(.map__aircraftcontainer--selected) {
    opacity: 0.38;
  }
  
  .map__aircraftcontainer--transparent:hover {
    opacity: 1;

    .map__tailtag--container {
      opacity: 1;
    }
  }

  .map__tailtag--container {
    position: absolute;
    display: inline-block;
    box-sizing: content-box;
    min-width: 100px;
    max-width: 300px;
    background-color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor}
    border: 1px solid ${({ theme }: StyledProps) => theme.tailTagBorderColor} !important;
    outline: 1px transparent !important;
    top: ${CIRCLE_SIZE - TAIL_TAG_CIRCLE_OVERLAP_PX}px;
    left: ${CIRCLE_SIZE - TAIL_TAG_CIRCLE_OVERLAP_PX}px;
  }

  .map__tailtag--divider {
    border-top: 1px solid ${({ theme }: StyledProps) => theme.terminalListBorderColor};
    border-bottom: none;
    border-right: none;
    border-left: none;
    margin: 0px 12px;
    position: absolute;
    width: calc(100% - 24px);
    box-sizing: border-box;
    opacity: 1;
    z-index: 3;
  }

  .map__tailtag--altitudecontainer {
    display: flex;
    align-items: center;

    > div {
      white-space: nowrap;
    }
    .map__tailtag--altitude {
      margin-top: 0px;
      display: inline-block;
      min-width: 0px;
      text-align: right;
      font-size: 14px;
    }
    > svg {
      fill: ${({ theme }: StyledProps) => theme.secondaryTextColor};
      height: 16px;
      width: 16px;
      margin-right: 4px;
      margin-bottom: -1px;
    }
  }

  .map__tailtag--takeofficon {
    display: none;
  }

  .map__tailtag--landingicon {
    display: none;
  }

  .map__tailtag--ondeckicon {
    display: none;
  }

  .map__tailtag--inflighticon {
    display: none;
  }

  .map__tailtag--row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-content: space-around;

    > div {
      line-height: 20px;
    }
  }

  .map__tailtag--header {
    height: ${TAIL_TAG_HEADER_HEIGHT}px;
    padding: 0px 12px;
    font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
    color: ${({ theme }: StyledProps) => theme.primaryTextColor};
    /* fixes problem where the right border of the tail tag does show up */
    border-right: 1px solid transparent;
    outline: 1px transparent !important;

    display: flex;
    justify-content: space-between;
    align-items: center;
    align-content: space-around;
    line-height: 24px;
  }

  .map__tailtag--tailname {
    font-size: 16px;
    margin-right: 10px;
    white-space: nowrap;
  }

  .map__tailtag {
    display: none;
    flex-direction: row;
    padding-top: 4px;
    padding-bottom: 2px;
    box-sizing: border-box;
    /* fixes problem where the right border of the tail tag does show up */
    border-right: 1px solid transparent;
    outline: 1px transparent !important;

    min-width: 150px;
    max-width: 300px;
    font-family: ${({ theme }: StyledProps) => theme.appFontFamily};
    font-size: 14px;
    color: ${({ theme }: StyledProps) => theme.primaryTextColor};

    .map__tailtag--detailcontainer {
      padding: 6px 13px 6px 13px;
      white-space: nowrap;
      flex-grow: 1;
    }

    .map__tailtag--steerablebeamprimaryterminallabel {
      display: none;

      .map__tailtag--steerablebeamprimaryterminallabel__badge {
        background-color: ${({ theme }: StyledProps) => theme.overlayBeams.networkGroups.hcx};
        color: ${({ theme }: StyledProps) => theme.primaryBackgroundColor};

        font-weight: 700;
        font-size: 12px;
        text-align: center;
        border-radius: 1px;
        box-sizing: border-box;
        padding: 2px 4px;
        line-height: 12px !important;
        height: 16px !important;
        min-width: 28px;
      }
    }
    .map__tailtag--name,
    .map__tailtag--lw_network {
      > span {
        margin-right: 2px;
        font-size: 16px;
        position: relative;
        top: 0px;
      }

      > svg {
        margin-right: 2px;
      }

      .map__tailtag--terminallabel {
        display: inline-block;
      }

      color: ${({ theme }: StyledProps) => theme.primaryTextColor};
      font-size: 14px;
      margin-right: 8px;
      margin-left: 0px;
      display: flex;
      align-items: center;
      box-sizing: border-box;
      line-height: 18px;
    }
  }

  .map__tailtag--sv_networks {
    color: ${({ theme }: StyledProps) => theme.primaryTextColor};
    font-size: 14px;
    margin-right: 8px;
    margin-left: 0px;
    align-items: center;
    box-sizing: border-box;
    line-height: 18px;

    .map__tailtag--terminallabel {
      display: inline-block;
    }

    > svg {
      margin-right: 2px;
    }
  }

  .${DISPLAY_BLOCK_CLASS} {
    display: block;
  }

  .${DISPLAY_NONE_CLASS} {
    display: none;
  }

  .ground__terminal__icon__path {
    fill: #141D24;
  }
`;

export default BaseMapWrapper;
